import React from "react";
import { Link } from "react-router-dom";
import { Car, UserCheck, Briefcase, ArrowRight, Check } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const SERVICES = [
  {
    icon: Car,
    title: "Self-Drive Car Rentals",
    summary: "Rent a vehicle on your own terms.",
    desc: "Ideal for business trips, tourism, or personal use. Flexible daily, weekly, and monthly rates with no hidden charges.",
    bullets: [
      "Daily, weekly & monthly rates",
      "No hidden charges — transparent pricing",
      "Wide range of vehicles to choose from",
      "Quick online booking",
    ],
    img: "https://images.unsplash.com/photo-1542362567-b07e54358753?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
  {
    icon: UserCheck,
    title: "Chauffeur-Drive Services",
    summary: "Travel in comfort with our professional drivers.",
    desc: "Perfect for airport transfers, corporate meetings, events, and city tours across Accra and beyond.",
    bullets: [
      "Professional & courteous drivers",
      "Airport transfers (Kotoka International)",
      "Corporate meetings & events",
      "City & inter-city tours",
    ],
    img: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
  {
    icon: Briefcase,
    title: "Long-Term & Corporate Hire",
    summary: "Tailored rental solutions for companies, NGOs & expatriates.",
    desc: "Competitive monthly rates, dedicated account support, and well-maintained vehicles for the long haul.",
    bullets: [
      "Competitive monthly rates",
      "Dedicated account manager",
      "Well-maintained vehicles",
      "Flexible contracts (3 / 6 / 12 months)",
    ],
    img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Our Services — Self-Drive, Chauffeur, Airport Pickup, Long-Term"
        description="Titan Car Rentals services: self-drive, chauffeur-drive, Kotoka airport pickup, long-term lease, corporate accounts, wedding & event hire. Serving Accra, Kumasi, Takoradi, Tema."
        path="/services"
        keywords="chauffeur Ghana, airport pickup Kotoka, long-term car lease Ghana, corporate car rental Accra, wedding car Ghana, self-drive Ghana"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])}
      />
      <section className="bg-[#0d2040] text-white py-20">
        <div className="container-titan">
          <span className="eyebrow text-[#ff8a93]">Our Services</span>
          <h1 className="h-section mt-3 text-white">Three ways Titan moves Ghana.</h1>
          <p className="text-white/70 mt-5 max-w-xl">
            From a Corolla for the day to a fully chauffeured fleet for your wedding — we&apos;ve built our services
            around how you actually drive Ghana.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-titan space-y-16">
          {SERVICES.map((s, i) => (
            <div
              key={s.title}
              className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
            >
              <div className={`${i % 2 === 1 ? "lg:[direction:ltr]" : ""}`}>
                <div className="aspect-[5/4] bg-[#0d2040]/10 overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className={`${i % 2 === 1 ? "lg:[direction:ltr]" : ""}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#e63946] flex items-center justify-center">
                    <s.icon size={18} className="text-white" />
                  </div>
                  <span className="eyebrow">0{i + 1} · Service</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0d2040]">{s.title}</h2>
                <p className="text-[#e63946] font-semibold mt-2">{s.summary}</p>
                <p className="text-gray-700 mt-4 leading-relaxed">{s.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-[#0d2040]">
                      <Check size={15} className="text-[#e63946] flex-shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
                <Link to="/fleet" className="btn-primary mt-7">
                  Browse fleet <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#e63946] text-white py-16">
        <div className="container-titan flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Need a custom service?</h2>
          <Link to="/quote" className="bg-white text-[#0d2040] font-bold uppercase tracking-wider px-7 py-4 hover:bg-[#0d2040] hover:text-white transition-all">
            Request a quote <ArrowRight size={16} className="inline ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
}
